import { cn } from "@/lib/utils";
import { forwardRef, TextareaHTMLAttributes, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface ScriptProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "onChange"> {
  value: string;
  tooltip: boolean;
  scriptPlaceholder?: string;
  className?: string;
  autoFocus?: boolean; // 新增可选的自动聚焦属性
  maxLineHeight?: number; // 新增可选的最大行数属性
  onChange: (text: string) => void;
}

export const Script = forwardRef<HTMLTextAreaElement, ScriptProps>(
  ({ value, tooltip, scriptPlaceholder, onChange, className, autoFocus = true, maxLineHeight = 5, ...textareaProps }, ref) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const [placeholder, setPlaceholder] = useState("");
    const [hasTyped, setHasTyped] = useState(false);
    const { t } = useTranslation();

    // 合并外部传入的 ref 和内部的 ref
    const mergedRef = (node: HTMLTextAreaElement | null) => {
      textareaRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    };

    const adjustTextareaHeight = () => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      const lineHeight = 20;
      const minHeight = lineHeight * 1;
      const maxHeight = lineHeight * maxLineHeight;

      // 重置高度
      textarea.style.height = "";
      // 设置新高度
      if (textarea.scrollHeight > textarea.offsetHeight) {
        textarea.style.height = `${Math.max(
          minHeight,
          Math.min(textarea.scrollHeight, maxHeight)
        )}px`;
      } else {
        textarea.style.height = `${textarea.offsetHeight}px`;
      }
    };

    // 自动聚焦
    useEffect(() => {
      if (autoFocus && textareaRef.current) {
        // 延迟聚焦，确保组件完全渲染
        const timer = setTimeout(() => {
          textareaRef.current?.focus();
        }, 100);

        return () => clearTimeout(timer);
      }
    }, [autoFocus]);

    // 打字效果 - 逐个字符
    useEffect(() => {
      const fullText = scriptPlaceholder || t("script.placeholder");
      if (!fullText) return;

      // 每次 scriptPlaceholder 变化时，先重置状态
      setPlaceholder("");
      let currentIndex = 0;
      let intervalId: NodeJS.Timeout;

      // 延迟启动
      const startTimer = setTimeout(() => {
        intervalId = setInterval(() => {
          setPlaceholder((prev) => {
            if (currentIndex < fullText.length) {
              const nextText = fullText.slice(0, currentIndex + 1);
              currentIndex++;
              return nextText;
            }
            clearInterval(intervalId);
            return prev;
          });
        }, 30);
      }, 300);

      // 清理函数，防止多个打字效果叠加
      return () => {
        clearTimeout(startTimer);
        clearInterval(intervalId);
      };
    }, [scriptPlaceholder, t]);

    useEffect(() => {
      adjustTextareaHeight();
    }, [value]);

    return (
      <div className="relative">
        <textarea
          ref={mergedRef}
          value={value}
          id="text"
          name="text"
          onChange={(e) => {
            onChange(e.target.value);
            requestAnimationFrame(adjustTextareaHeight);
          }}
          placeholder={placeholder}
          minLength={100}
          maxLength={2400}
          className={cn(
            "overflow-y-auto w-full h-5 px-4 border-0 resize-none focus:outline-none text-sm text-[rgba(29,41,57,1)] placeholder:text-[#98A2B3] placeholder:select-none",
            className
          )}
          {...textareaProps}
        />
        {tooltip && (
          <div className="absolute -top-2 left-4 px-2 py-1 w-fit transform -translate-y-[100%] text-sm text-white font-medium bg-[rgba(117,66,247,1)] rounded-sm rounded-bl-none">
            {t("script.tooltip")}
          </div>
        )}
      </div>
    );
  }
);
